import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { getCurrentSiteHost } from '../../utils/siteHost';
import { useQuery } from '@apollo/client';
import {
  GET_MONTHLY_RENTAL_CARS_NEAR_ME,
  GET_WEEKLY_RENTAL_CARS_NEAR_ME,
} from '../../services/carService/queries';
import { useGetMyCarsV2HomeQuery } from '../../services/carService/getMyCarsV2';
import {
  FEATURED_CARS_DISPLAY_COUNT,
  homepageCategoryToApi,
  useGetFeaturedCarsQuery,
} from '../../services/carService/getFeaturedCars';
import { GET_MONTHLY_RENTAL_HOT_LOCATION_SECTIONS } from '../../services/hotLocationService/queries';
import { useNavigate } from 'react-router-dom';
import { Role } from '../../../store/roleStore';
import { roleStore } from '../../../store/roleStore';
import { useSnapshot } from 'valtio';
import CarsSlideShow from '../../components/cars/carsSlideShow';
import { CarCardButtonType } from '../../enums';
import { DesignTokens } from '../../styles/colors';
import {
  filterHomeCarsByCategory,
  homeCarouselCarsIdsKey,
  homeCarouselDisplayCars,
} from '../../helpers/homeCarouselDisplay';
import { HomeCarouselCar } from '../../helpers/homeCarouselCarTypes';
import { CarCardPriceMode, carsForPriceMode } from '../../utils/carCardPrice';
import { useBrandRules } from '../../hooks/useHostCapabilities';
import { browserTimeZone, MonthlySearchPlace, monthlyViewAllPath } from '../../helpers/monthlyTrip';

const TopCarsContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    mx-auto
    flex
    flex-col
    items-stretch
    justify-start
    px-2
    md:px-4
    lg:px-0
    mb-10
    box-border
    overflow-x-hidden
  `};
  max-width: ${DesignTokens.container.maxWidth};
`;

const SectionStack = styled.div`
  ${tw`w-full max-w-full min-w-0 overflow-x-hidden`}
`;

const SectionGap = styled.div`
  height: 12px;
`;

const ErrorContainer = styled.div`
  ${tw`w-full mt-9 mx-auto max-w-screen-xl px-4 md:px-6 lg:px-0 text-center text-sm py-14`}
  color: var(--color-text-secondary);
`;

type MonthlyCoords = {
  latitude: number;
  longitude: number;
};

type HotLocationSection = {
  hotLocation: {
    id: string;
    city: string;
    state: string;
    latitude?: number | null;
    longitude?: number | null;
    index: number;
    amount: number;
  };
  cars: HomeCarouselCar[];
};

export function TopCars({
  selectedCategory = 'all',
  priceMode = 'daily',
  brandPriceMode = 'daily',
}: {
  selectedCategory?: string;
  /** Spec 022, 028: the selected home tab, Daily, Weekly or Monthly (guests only). */
  priceMode?: CarCardPriceMode;
  /** Spec 022 D14: `weekly` adds "Weekly rentals near you". */
  brandPriceMode?: CarCardPriceMode;
}) {
  const navigate = useNavigate();
  const { role } = useSnapshot(roleStore);
  const isHost = role === Role.HOST;
  const isGuest = role === Role.GUEST;
  const [coords, setCoords] = useState<MonthlyCoords | null>(null);
  const [hasDeviceLocation, setHasDeviceLocation] = useState(false);
  const [geoResolved, setGeoResolved] = useState(isHost);
  const { monthlyRentalOffered, effectiveMinimumAge } = useBrandRules();

  /** Spec 028: "View all" on a monthly section opens the results in Months mode (1 month). */
  const monthlyViewAll = (place: MonthlySearchPlace | null, testId: string) =>
    monthlyRentalOffered && place
      ? {
          testId,
          onClick: () =>
            navigate(monthlyViewAllPath(place, new Date(), Math.max(25, effectiveMinimumAge))),
        }
      : undefined;

  useEffect(() => {
    if (isHost) {
      return;
    }

    if (!navigator.geolocation) {
      setGeoResolved(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        setCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setHasDeviceLocation(true);
        setGeoResolved(true);
      },
      () => {
        setCoords(null);
        setHasDeviceLocation(false);
        setGeoResolved(true);
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 },
    );
  }, [isHost]);

  const siteHost = getCurrentSiteHost();
  const featuredCategory = homepageCategoryToApi(selectedCategory);

  const {
    data: featuredCarsData,
    loading: featuredCarsLoading,
    error: featuredCarsError,
  } = useGetFeaturedCarsQuery({
    skip: isHost,
    variables: {
      input: {
        type: 'CAR',
        siteHost: siteHost || undefined,
        category: featuredCategory,
        limit: FEATURED_CARS_DISPLAY_COUNT,
      },
    },
  });

  const featuredSettled = !featuredCarsLoading;

  const {
    data: myCarsData,
    loading: myCarsLoading,
    error: myCarsLoadingError,
  } = useGetMyCarsV2HomeQuery({
    skip: !isHost,
    variables: { input: { page: 1, limit: 10 } },
  });

  const {
    data: monthlyCarsData,
    loading: monthlyCarsLoading,
    error: monthlyCarsError,
  } = useQuery(GET_MONTHLY_RENTAL_CARS_NEAR_ME, {
    skip: isHost || !featuredSettled || !geoResolved || !hasDeviceLocation || !coords,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        latitude: coords?.latitude ?? 0,
        longitude: coords?.longitude ?? 0,
        minRadiusMiles: 0,
        maxRadiusMiles: 10,
        driverAge: 25,
        type: 'CAR',
        siteHost: siteHost || undefined,
      },
    },
  });

  // Spec 022 D14: every car near the guest, only on a brand whose cards show the week price.
  const showWeeklySection = isGuest && brandPriceMode === 'weekly';
  const {
    data: weeklyCarsData,
    loading: weeklyCarsLoading,
    error: weeklyCarsError,
  } = useQuery(GET_WEEKLY_RENTAL_CARS_NEAR_ME, {
    skip: !showWeeklySection || !featuredSettled || !geoResolved || !hasDeviceLocation || !coords,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        latitude: coords?.latitude ?? 0,
        longitude: coords?.longitude ?? 0,
        minRadiusMiles: 0,
        maxRadiusMiles: 10,
        driverAge: 25,
        type: 'CAR',
        siteHost: siteHost || undefined,
      },
    },
  });

  const {
    data: hotLocationSectionsData,
    loading: hotLocationSectionsLoading,
    error: hotLocationSectionsError,
  } = useQuery(GET_MONTHLY_RENTAL_HOT_LOCATION_SECTIONS, {
    skip: isHost || !featuredSettled,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        maxRadiusMiles: 100,
        driverAge: 25,
        type: 'CAR',
        siteHost: siteHost || undefined,
      },
    },
  });

  const cars = isHost ? myCarsData?.getMyCarsV2?.items : featuredCarsData?.getFeaturedCars;
  const carsLoading = isHost ? myCarsLoading : featuredCarsLoading;
  const loadingCarsError = isHost ? myCarsLoadingError : featuredCarsError;

  const handleCarClick = (carId: string) => {
    if (isHost) {
      navigate(`/cars/host/view/${carId}`);
    } else if (isGuest) {
      navigate(`/cars/guest/view/${carId}`);
    }
  };

  const handleButtonClicked = (carId: string, buttonType: CarCardButtonType) => {
    if (buttonType === CarCardButtonType.RENT_NOW) {
      handleCarClick(carId);
    } else if (buttonType === CarCardButtonType.BOOKING_RECORD) {
      navigate(`/cars/host/bookings/${carId}`);
    }
  };

  const renderButtons = (car: HomeCarouselCar) => [
    {
      text: isHost ? 'Edit' : 'Rent Now',
      onClick: () => handleButtonClicked(car.id, CarCardButtonType.RENT_NOW),
    },
    {
      text: 'Booking Record',
      onClick: () => handleButtonClicked(car.id, CarCardButtonType.BOOKING_RECORD),
    },
  ];

  const featuredDisplayCars = useMemo(() => {
    if (isHost) {
      return filterHomeCarsByCategory((cars ?? []) as HomeCarouselCar[], selectedCategory);
    }
    // Spec 022 D13: under Weekly, cars capped under 7 days are left out.
    return carsForPriceMode((cars ?? []) as HomeCarouselCar[], isGuest ? priceMode : 'daily');
  }, [cars, selectedCategory, isHost, isGuest, priceMode]);

  const weeklyCarsFiltered = useMemo(
    () =>
      carsForPriceMode(
        filterHomeCarsByCategory(
          (weeklyCarsData?.getCarsNearMe ?? []) as HomeCarouselCar[],
          selectedCategory,
        ),
        'weekly',
      ),
    [weeklyCarsData, selectedCategory],
  );

  const weeklyDisplayCars = useMemo(
    () => homeCarouselDisplayCars(weeklyCarsFiltered, { isHost: false }),
    [weeklyCarsFiltered],
  );

  const monthlyCarsFiltered = useMemo(
    () =>
      filterHomeCarsByCategory(
        (monthlyCarsData?.getMonthlyRentalCarsNearMe ?? []) as HomeCarouselCar[],
        selectedCategory,
      ),
    [monthlyCarsData, selectedCategory],
  );

  const monthlyCarsKey = homeCarouselCarsIdsKey(monthlyCarsFiltered);

  const monthlyDisplayCars = useMemo(
    () => homeCarouselDisplayCars(monthlyCarsFiltered, { isHost: false }),
    [monthlyCarsFiltered, monthlyCarsKey],
  );

  const hotLocationSections = useMemo(() => {
    return (
      (hotLocationSectionsData?.monthlyRentalHotLocationSections ?? []) as HotLocationSection[]
    )
      .map(section => {
        const filtered = filterHomeCarsByCategory(section.cars ?? [], selectedCategory);
        return {
          ...section,
          cars: homeCarouselDisplayCars(filtered, { isHost: false }),
        };
      })
      .filter(section => section.cars.length > 0);
  }, [hotLocationSectionsData, selectedCategory]);

  if (loadingCarsError) {
    return <ErrorContainer>Error loading cars</ErrorContainer>;
  }
  if (!carsLoading && !cars) {
    return <ErrorContainer>No cars found</ErrorContainer>;
  }

  const showMonthlyNearMe =
    isGuest &&
    hasDeviceLocation &&
    geoResolved &&
    !monthlyCarsLoading &&
    !monthlyCarsError &&
    monthlyDisplayCars.length > 0;

  const showWeeklyNearMe =
    showWeeklySection &&
    hasDeviceLocation &&
    geoResolved &&
    !weeklyCarsLoading &&
    !weeklyCarsError &&
    weeklyDisplayCars.length > 0;

  const title = isHost ? 'My Car List' : 'Featured Cars';

  return (
    <TopCarsContainer>
      <CarsSlideShow
        title={title}
        cars={featuredDisplayCars}
        carsLoading={carsLoading}
        carsError={loadingCarsError}
        renderButtons={renderButtons}
        onCarClick={handleCarClick}
        priceMode={isGuest ? priceMode : 'daily'}
      />
      {isGuest && (
        <>
          {showWeeklyNearMe && (
            <SectionStack>
              <SectionGap />
              <CarsSlideShow
                title="Weekly rentals near you"
                cars={weeklyDisplayCars}
                carsLoading={weeklyCarsLoading}
                carsError={weeklyCarsError}
                renderButtons={renderButtons}
                onCarClick={handleCarClick}
                priceMode="weekly"
              />
            </SectionStack>
          )}
          {showMonthlyNearMe && (
            <SectionStack>
              <SectionGap />
              <CarsSlideShow
                title="Monthly rentals near you"
                cars={monthlyDisplayCars}
                carsLoading={monthlyCarsLoading}
                carsError={monthlyCarsError}
                renderButtons={renderButtons}
                onCarClick={handleCarClick}
                priceMode="monthly"
                viewAll={monthlyViewAll(
                  coords && {
                    ...coords,
                    address: 'Current location',
                    timezone: browserTimeZone(),
                  },
                  'home-monthly-view-all-near-me',
                )}
              />
            </SectionStack>
          )}
          {hotLocationSections.map(section => (
            <SectionStack key={section.hotLocation.id}>
              <SectionGap />
              <CarsSlideShow
                title={`Monthly car rentals in ${section.hotLocation.city}`}
                cars={section.cars}
                carsLoading={hotLocationSectionsLoading}
                carsError={hotLocationSectionsError}
                renderButtons={renderButtons}
                onCarClick={handleCarClick}
                priceMode="monthly"
                viewAll={monthlyViewAll(
                  section.hotLocation.latitude != null && section.hotLocation.longitude != null
                    ? {
                        latitude: section.hotLocation.latitude,
                        longitude: section.hotLocation.longitude,
                        address: `${section.hotLocation.city}, ${section.hotLocation.state}`,
                        timezone: browserTimeZone(),
                      }
                    : null,
                  `home-monthly-view-all-${section.hotLocation.id}`,
                )}
              />
            </SectionStack>
          ))}
        </>
      )}
    </TopCarsContainer>
  );
}
